import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Save, Trash2, X, Target, Users, Globe } from 'lucide-react';

export default function MissionTab({ divisionId }) {
  const { user } = useAuth();
  const [mission, setMission] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showComForm, setShowComForm] = useState(false);
  const [comForm, setComForm] = useState({ name: '', purpose: '', location: '', status: 'active', needs: '', custodian: '' });
  const [loading, setLoading] = useState(true);

  const entityMap = { lc: 'les-concierges', 'cc-clubs': 'club-concierge', 'cc-ops': 'cc-ops', tdb: 'tdb', me: 'mindescapes', 'club-ops': 'club-ops' };
  const entityId = entityMap[divisionId] || divisionId;

  useEffect(() => {
    Promise.all([
      api.get(`/missions/${entityId}`),
      api.get('/missions/community/all'),
    ]).then(([mRes, cRes]) => {
      setMission(mRes.data);
      setEditForm(mRes.data);
      setCommunities(cRes.data.filter(c => !divisionId || c.entity === entityId || c.entity === divisionId));
    }).finally(() => setLoading(false));
  }, [divisionId]);

  const saveMission = async () => {
    await api.put(`/missions/${entityId}`, editForm);
    setMission(editForm);
    setEditing(false);
  };

  const addCommunity = async (e) => {
    e.preventDefault();
    const r = await api.post('/missions/community', { ...comForm, entity: entityId });
    setCommunities(prev => [...prev, r.data]);
    setComForm({ name: '', purpose: '', location: '', status: 'active', needs: '', custodian: '' });
    setShowComForm(false);
  };

  const deleteCommunity = async (id) => {
    if (!window.confirm('Remove this commitment?')) return;
    await api.delete(`/missions/community/${id}`);
    setCommunities(prev => prev.filter(c => c.id !== id));
  };

  if (loading) return <div className="py-8 text-center text-sm" style={{ color: '#6B7280' }}>Loading...</div>;

  return (
    <div className="space-y-5" data-testid="mission-tab">
      {/* Mission Statement */}
      <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Target size={16} style={{ color: '#2A7B88' }} />
            <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Entity Mission</h4>
          </div>
          {!editing && (
            <button onClick={() => setEditing(true)} className="text-xs px-2 py-1 rounded border hover:bg-gray-50" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Edit</button>
          )}
        </div>

        {editing ? (
          <div className="space-y-3">
            <input value={editForm.entity_name || ''} onChange={e => setEditForm(p => ({...p, entity_name: e.target.value}))} placeholder="Entity name" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            <textarea value={editForm.mission_statement || ''} onChange={e => setEditForm(p => ({...p, mission_statement: e.target.value}))} placeholder="Mission statement — what is this entity's purpose?" rows={3} className="w-full px-3 py-2 rounded-md border text-sm resize-none" style={{ borderColor: '#E5E1DC', fontFamily: 'Georgia, serif' }} />
            <div className="grid grid-cols-2 gap-3">
              <input value={editForm.year_founded || ''} onChange={e => setEditForm(p => ({...p, year_founded: e.target.value}))} placeholder="Year founded" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
              <input value={editForm.current_head || ''} onChange={e => setEditForm(p => ({...p, current_head: e.target.value}))} placeholder="Current head" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="flex gap-2">
              <button onClick={saveMission} className="flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-white" style={{ backgroundColor: '#1B2A4A' }}><Save size={13} /> Save</button>
              <button onClick={() => { setEditForm(mission); setEditing(false); }} className="px-3 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <div>
            {mission?.mission_statement ? (
              <p className="text-sm leading-relaxed" style={{ color: '#1B2A4A', fontFamily: 'Georgia, serif' }}>{mission.mission_statement}</p>
            ) : (
              <p className="text-sm italic" style={{ color: '#9CA3AF' }}>No mission statement set. Click Edit to define this entity's purpose.</p>
            )}
            {mission?.year_founded && <p className="text-xs mt-2" style={{ color: '#6B7280' }}>Founded: {mission.year_founded} | Head: {mission.current_head || 'TBD'}</p>}
          </div>
        )}
      </div>

      {/* Purpose Ventures */}
      <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center gap-2 mb-3">
          <Globe size={16} style={{ color: '#2D8B5E' }} />
          <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Purpose Ventures</h4>
        </div>
        {mission?.purpose_ventures?.length > 0 ? (
          <div className="space-y-2">
            {mission.purpose_ventures.map((v, i) => (
              <div key={i} className="p-2 rounded-md text-sm" style={{ backgroundColor: '#F7F5F2' }}>
                <p className="font-medium" style={{ color: '#1B2A4A' }}>{v.name || v}</p>
                {v.purpose && <p className="text-xs" style={{ color: '#6B7280' }}>{v.purpose}</p>}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm italic" style={{ color: '#9CA3AF' }}>No purpose ventures defined yet. Edit the mission to add them.</p>
        )}
      </div>

      {/* Community Commitments */}
      <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Users size={16} style={{ color: '#BA7517' }} />
            <h4 className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Community Commitments</h4>
          </div>
          <button onClick={() => setShowComForm(!showComForm)} className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#2A7B88' }}><Plus size={12} /> Add</button>
        </div>

        {showComForm && (
          <form onSubmit={addCommunity} className="mb-3 p-3 rounded-lg space-y-2" style={{ backgroundColor: '#F7F5F2' }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={comForm.name} onChange={e => setComForm(p => ({...p, name: e.target.value}))} placeholder="Name (e.g. U-17 Football Team)" required className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
              <input value={comForm.location} onChange={e => setComForm(p => ({...p, location: e.target.value}))} placeholder="Location" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
              <input value={comForm.purpose} onChange={e => setComForm(p => ({...p, purpose: e.target.value}))} placeholder="Purpose" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
              <input value={comForm.custodian} onChange={e => setComForm(p => ({...p, custodian: e.target.value}))} placeholder="Custodian" className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-3 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }}>Add</button>
              <button type="button" onClick={() => setShowComForm(false)} className="px-3 py-1.5 rounded-md text-xs border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            </div>
          </form>
        )}

        {communities.length > 0 ? (
          <div className="space-y-2">
            {communities.map(c => (
              <div key={c.id} className="flex items-center justify-between p-2 rounded-md" style={{ backgroundColor: '#F7F5F2' }}>
                <div>
                  <p className="text-sm font-medium" style={{ color: '#1B2A4A' }}>{c.name}</p>
                  <p className="text-xs" style={{ color: '#6B7280' }}>{c.purpose} {c.location && `| ${c.location}`} {c.custodian && `| Custodian: ${c.custodian}`}</p>
                </div>
                <button onClick={() => deleteCommunity(c.id)} className="p-1 hover:bg-gray-200 rounded"><Trash2 size={13} style={{ color: '#D4726A' }} /></button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm italic" style={{ color: '#9CA3AF' }}>No community commitments yet (e.g. football teams, farm projects).</p>
        )}
      </div>
    </div>
  );
}
